import React, { Suspense } from "react";
import { Route, Router, Redirect } from "react-router-dom";
import CodeSplitter from "helpers/CodeSplitter";
import { ToastBar, Toaster } from "react-hot-toast";
import Routers from "./routers";
import * as Layout from "../layouts";
import { history, defaultValidator } from "helpers/utis";
import logo from "../assets/images/sobhaLogo.png";



const LoadingFallback = () => (
  <div
    style={{
      height: "100vh",
      width: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }}
  >
    <img
      src={logo}
      alt="logo"
      style={{ height: "150px", width: "350px" }}
    />
  </div>
);

const ToastContent = ({ type, icon, message }) => (
  <div
    className={`custom-toast-${type} d-flex align-items-center custom-toast-item`}
  >
    {icon}
    {message}
  </div>
);

const createToastContentRenderer = (toast) => {
  return ({ icon, message }) => (
    <ToastContent type={toast.type} icon={icon} message={message} />
  );
};

const renderToast = (toast) => (
  <ToastBar toast={toast} style={{ padding: 0, overflow: "hidden" }}>
    {createToastContentRenderer(toast)}
  </ToastBar>
);

const AppToaster = () => (
  <Toaster
    position="bottom-right"
    gutter={8}
    reverseOrder
    containerClassName="custom-toast"
    containerStyle={{ right: "40px", bottom: "50px" }}
  >
    {renderToast}
  </Toaster>
);

const joinRoutePath = (parentPath = "", childPath = "") =>
  `${parentPath}${childPath}`;

const shouldRedirect = (route, routeProps) => {
  return Boolean(
    route?.redirect && routeProps?.location?.pathname === route?.path,
  );
};

const LazyPage = ({ componentName, routeProps }) => {
  const PageComponent = CodeSplitter.getComponent(componentName);

  return <PageComponent {...routeProps} />;
};

const createPageRenderer = (componentName) => {
  return (routeProps) => (
    <LazyPage componentName={componentName} routeProps={routeProps} />
  );
};

const ChildRoute = ({ parentPath, childRoute }) => {
  const {
    componentPath: childComponentPath,
    name: childrenName = "",
    path: childrenPath,
    exact: childrenExact = false,
  } = childRoute;

  const routePath = joinRoutePath(parentPath, childrenPath);

  CodeSplitter.addComponent(childComponentPath, childrenName);

  return (
    <Route
      path={routePath}
      exact={childrenExact}
      key={routePath}
      render={createPageRenderer(childrenName)}
    />
  );
};

const getChildRouteKey = (parentPath, childRoute) => {
  return joinRoutePath(parentPath, childRoute?.path);
};

const createChildRouteRenderer = (parentPath) => {
  return (childRoute) => (
    <ChildRoute
      key={getChildRouteKey(parentPath, childRoute)}
      parentPath={parentPath}
      childRoute={childRoute}
    />
  );
};

const LayoutRouteContent = ({ route, routeProps }) => {
  if (shouldRedirect(route, routeProps)) {
    return <Redirect to={route.redirect} />;
  }

  const LayoutComponent = Layout[route.component];
  const renderChildRoute = createChildRouteRenderer(route.path);

  return (
    <LayoutComponent {...routeProps}>
      {route.childrens.map(renderChildRoute)}
    </LayoutComponent>
  );
};

const createLayoutRouteRenderer = (route) => {
  return (routeProps) => (
    <LayoutRouteContent route={route} routeProps={routeProps} />
  );
};

const LayoutRoute = ({ route }) => (
  <Route
    path={route.path}
    exact={route.exact}
    key={route.path}
    render={createLayoutRouteRenderer(route)}
  />
);

const StandaloneRouteContent = ({ route, routeProps }) => {
  if (route.component) {
    const PageComponent = CodeSplitter.getComponent(route.name);

    return <PageComponent {...routeProps} />;
  }

  if (shouldRedirect(route, routeProps)) {
    return <Redirect to={route.redirect} />;
  }

  return <div />;
};

const createStandaloneRouteRenderer = (route) => {
  return (routeProps) => (
    <StandaloneRouteContent route={route} routeProps={routeProps} />
  );
};

const getStandaloneRouteKey = (route) => {
  return route.path || defaultValidator(route.component, 2322);
};

const StandaloneRoute = ({ route }) => {
  CodeSplitter.addComponent(route.componentPath, route.name);

  return (
    <Route
      path={route.path}
      exact={route.exact}
      key={getStandaloneRouteKey(route)}
      render={createStandaloneRouteRenderer(route)}
    />
  );
};

const RouteItem = ({ route }) => {
  const hasChildren = route.childrens?.length > 0;

  if (hasChildren) {
    return <LayoutRoute route={route} />;
  }

  return <StandaloneRoute route={route} />;
};

const renderRouteItem = (route) => <RouteItem key={route.path} route={route} />;

const RoutesClass = () => {
  return (
    <Router history={history}>
      <Suspense fallback={<LoadingFallback />}>
        {Routers.map(renderRouteItem)}
      </Suspense>

      <AppToaster />
    </Router>
  );
};

export default RoutesClass;